import { useCallback, useState } from "react";

import { Button, Stack, TextField, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { AddressAutocompleteForm } from "components/AddressAutocompleteForm";
import { ErrorTypography } from "components/ErrorTypography";

import { ClientAppEvent } from "../../../../constants";
import { useAuthContext, useWriterComponentState } from "../../../../hooks";
import { CompanyBusinessType } from "../CompanyBusinessType";

const postHogEventByCompanyType: Record<"CARRIER" | "SHIPPER", ClientAppEvent> =
  {
    CARRIER: ClientAppEvent.CARRIER_COMPANY_CREATED,
    SHIPPER: ClientAppEvent.SHIPPER_COMPANY_CREATED,
  };

const initialAddressData = {
  address: "",
  city: "",
  province: "",
  country: "",
  postal: "",
};

const initialRemainingCompanyData = {
  description: "",
};

interface CreateCompanyFormLabelProps {
  text: string;
}
function CreateCompanyFormLabel(props: CreateCompanyFormLabelProps) {
  const { text } = props;
  return (
    <Typography variant="h6" fontWeight="bold">
      {text}
    </Typography>
  );
}

interface Props {
  companyData: { name: string };
  handleSearchCompanySelected: () => void;
  handleResetCompanyData: () => void;
  companyType: "CARRIER" | "SHIPPER";
}

export function CreateCompanyForm(props: Props) {
  const {
    companyData,
    handleSearchCompanySelected,
    handleResetCompanyData,
    companyType,
  } = props;

  const { refreshUser } = useAuthContext();
  const posthog = usePostHog();
  const [addressData, setAddressData] =
    useState<Partial<CompanyAddress>>(initialAddressData);
  const [remainingCompanyData, setRemainingCompanyData] = useState<
    Partial<Company>
  >(initialRemainingCompanyData);
  const [businessType, setBusinessType] = useState<BusinessTypeValue>("");

  const normalizedPostal = addressData.postal?.toUpperCase().replace(/ /g, "");
  const { loading, error, write } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/companies",
      body: {
        address: { ...addressData, postal: normalizedPostal },
        name: companyData.name,
        company_type: companyType,
        description: remainingCompanyData.description,
        ...(businessType && { business_type: businessType }),
      },
    },
    errorMessage: "Failed to create company!",
    errorMessageKey: "message",
  });

  const onAddressValueChanged = useCallback(
    (key: keyof CompanyAddress, value: string): void => {
      setAddressData((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const onCompanyValueChanged = (key: keyof Company, value: string): void => {
    setRemainingCompanyData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onCreateCompany = (): void => {
    write(async () => {
      posthog?.capture(postHogEventByCompanyType[companyType]);
      /*
        Refreshing the user causes mounting the dashboard,
        since the user will be assigned an admin role linked
        to the company upon company creation
      */
      await refreshUser();
    });
  };

  const REQUIRED_ADDRESS_KEYS: (keyof typeof addressData)[] = [
    "address",
    "city",
    "postal",
    "country",
  ];
  const isAddressDataIncomplete =
    REQUIRED_ADDRESS_KEYS.some(
      (key) => (addressData[key] ?? "").length === 0,
    ) ||
    ((addressData.country === "CA" || addressData.country === "US") &&
      !addressData.province);

  const createCompanyDisabled =
    loading ||
    isAddressDataIncomplete ||
    (companyType === "CARRIER" && !businessType);

  return (
    <Stack spacing={3} direction="column">
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        Create your company profile
      </Typography>
      <CreateCompanyFormLabel text="Company Name" />
      <Typography variant="h6">{companyData.name}</Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button variant="outlined" onClick={handleResetCompanyData}>
          Made a mistake? Reset Company Info
        </Button>
        <Typography variant="body1" fontWeight="bold">
          or
        </Typography>
        <Button variant="outlined" onClick={handleSearchCompanySelected}>
          Back to Company Search
        </Button>
      </Stack>
      <CreateCompanyFormLabel text="Company Description" />
      <TextField
        required={false}
        label="Description"
        multiline
        value={remainingCompanyData.description ?? ""}
        inputProps={{ maxLength: 1024 }}
        helperText={`${(remainingCompanyData.description ?? "").length}/1024`}
        onChange={(e) => onCompanyValueChanged("description", e.target.value)}
      />
      <CreateCompanyFormLabel text="Primary Address" />
      <AddressAutocompleteForm onValueChanged={onAddressValueChanged} />
      <CompanyBusinessType
        isCarrier={companyType === "CARRIER"}
        businessType={businessType}
        onBusinessTypeChange={setBusinessType}
      />
      <Button
        disabled={createCompanyDisabled}
        variant="contained"
        onClick={onCreateCompany}
      >
        Create Company
      </Button>
      <ErrorTypography error={error} />
    </Stack>
  );
}
